import { useEffect, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  IconButton,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import {
  FlagOutlined,
  StoreOutlined as OrganizationIcon,
} from '@mui/icons-material';
import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import { Organization } from '../../types/organizations';
import { useAppSelector, useResponsive } from '@/hooks';
import { useAccessControl } from '@/features/access-control';
import { DataGrid } from '@/components';
import { RoleBadge } from './components';
import { useFetchAccessQuery } from '../auth/api/accessApi';
import { useFetchUserOrganizationsQuery } from '../organizations/api/organizationsApi';
import { skipToken } from '@reduxjs/toolkit/dist/query';
import PageHeader from '@/components/PageHeader';
import routes from '@/constants/routes';

const Organizations = () => {
  const navigate = useNavigate();
  const { isMobile } = useResponsive();
  const { getRole } = useAccessControl();
  const uid = useAppSelector((state) => state?.auth?.user?.uid);
  const userAccess = useFetchAccessQuery(uid ?? skipToken);
  const userOrganizations = useFetchUserOrganizationsQuery(
    userAccess?.data ?? skipToken,
    {
      skip: !userAccess?.data?.length,
    }
  );

  const columns: GridColDef[] = useMemo(
    () => [
      {
        flex: 2,
        field: 'title',
        headerName: 'Nombre',
        width: 150,
        hideSortIcons: true,
        disableColumnMenu: true,
        renderCell: (params: GridRenderCellParams<Organization>) => (
          <Stack spacing={1} direction="row" alignItems="center">
            <OrganizationIcon />
            <Link
              to={`/comercios/${params.id}/ordenes/asignadas`}
              style={{ textDecoration: 'none' }}
            >
              {params.value}
            </Link>
          </Stack>
        ),
      },
      {
        flex: 1,
        field: 'rol',
        headerName: 'Rol',
        width: 150,
        hideSortIcons: true,
        disableColumnMenu: true,
        renderCell: (params: GridRenderCellParams<Organization>) => {
          const role = getRole(params.row.id);
          return <RoleBadge role={role} />;
        },
      },
      {
        flex: 1,
        field: 'landingShortcut',
        headerName: 'Landing Page',
        width: 150,
        hideSortIcons: true,
        disableColumnMenu: true,
        align: 'center',
        headerAlign: 'center',
        renderCell: (params: GridRenderCellParams<Organization>) => (
          <IconButton
            component={Link}
            to={`/comercios/${params.row.id}`}
            target="_blank"
          >
            <FlagOutlined color="primary" />
          </IconButton>
        ),
      },
    ],
    [getRole]
  );

  useEffect(() => {
    if (userOrganizations?.data?.[0]) {
      navigate(
        routes.organizations.details.replace(
          ':organizationId',
          userOrganizations.data[0].id
        )
      );
    }
  }, [navigate, userOrganizations?.data]);

  if (userAccess.isFetching || userOrganizations.isFetching) {
    return 'Loading...';
  }

  if (!userAccess.data || !userOrganizations.data) {
    return 'Empty';
  }

  return (
    <Box>
      {isMobile ? (
        <Stack spacing={2}>
          {userOrganizations.data.map((organization) => (
            <Card key={organization.id}>
              <CardContent>
                <img
                  src={organization.logo}
                  alt={organization.title}
                  height="auto"
                  width="200px"
                />
                <Typography variant="h3">{organization.title}</Typography>
              </CardContent>
              <CardActions>
                <Button
                  component={Link}
                  to={`/comercios/${organization.id}/ordenes/asignadas`}
                >
                  Ver Mas
                </Button>
                {organization?.industry === 'food-and-beverages' ? (
                  <Button
                    component={Link}
                    to={`/comercios/${organization.id}`}
                    target="_blank"
                    variant="outlined"
                  >
                    Menú
                  </Button>
                ) : null}
              </CardActions>
            </Card>
          ))}
        </Stack>
      ) : (
        <>
          <Stack gap={2}>
            <PageHeader title="Comercios" />
            <Paper elevation={0}>
              <DataGrid rows={userOrganizations.data} columns={columns} />
            </Paper>
          </Stack>
        </>
      )}
    </Box>
  );
};

export default Organizations;
